import React, { useState, useMemo } from 'react';
import { List, useTable, EditButton, ShowButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Button, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { ContentPageDto, ContentStatus, ContentType } from '@wellverse/shared-ts';
import { useCustomMutation, useNotification } from '@refinedev/core';

const statusColors: Record<ContentStatus, string> = {
  [ContentStatus.DRAFT]: '#898989',
  [ContentStatus.PUBLISHED]: '#3ecf8e',
  [ContentStatus.ARCHIVED]: '#f5a623',
};

export const ContentList: React.FC = () => {
  const { tableProps, tableQueryResult } = useTable<ContentPageDto>({
    resource: 'content',
    pagination: { current: 1, pageSize: 20 },
  });
  const { open } = useNotification();
  const { mutate } = useCustomMutation();

  const [searchText, setSearchText] = useState('');

  const filteredData = useMemo(() => {
    if (!searchText) return tableProps.dataSource;
    const lower = searchText.toLowerCase();
    return tableProps.dataSource?.filter((item: any) =>
      item.title?.toLowerCase().includes(lower) ||
      item.slug?.toLowerCase().includes(lower)
    );
  }, [tableProps.dataSource, searchText]);

  const handlePublish = (id: string) => {
    mutate(
      {
        url: `/content/${id}/publish`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: () => {
          open?.({ type: 'success', message: 'Published successfully' });
          tableQueryResult?.refetch();
        },
        onError: () => {
          open?.({ type: 'error', message: 'Failed to publish' });
        },
      }
    );
  };

  const handleArchive = (id: string) => {
    mutate(
      {
        url: `/content/${id}/archive`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: () => {
          open?.({ type: 'success', message: 'Archived successfully' });
          tableQueryResult?.refetch();
        },
        onError: () => {
          open?.({ type: 'error', message: 'Failed to archive' });
        },
      }
    );
  };

  return (
    <List resource="content">
      <div style={{ marginBottom: 16 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by title, slug..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 320 }}
          allowClear
        />
      </div>
      <Table {...tableProps} dataSource={filteredData} rowKey="id">
        <Table.Column dataIndex="title" title="Title" />
        <Table.Column dataIndex="slug" title="Slug" />
        <Table.Column
          dataIndex="type"
          title="Type"
          filters={Object.values(ContentType).map((t) => ({ text: t, value: t }))}
          onFilter={(value, record: any) => record.type === value}
          render={(type: ContentType) => type}
        />
        <Table.Column
          dataIndex="status"
          title="Status"
          filters={Object.values(ContentStatus).map((s) => ({ text: s, value: s }))}
          onFilter={(value, record: any) => record.status === value}
          render={(status: ContentStatus) => (
            <Tag
              style={{
                borderColor: statusColors[status],
                color: statusColors[status],
                background: `${statusColors[status]}15`,
                borderRadius: 9999,
                padding: '2px 12px',
                fontWeight: 500,
              }}
            >
              {status}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="publishedAt"
          title="Published At"
          render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
        />
        <Table.Column
          dataIndex="updatedAt"
          title="Updated At"
          render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_, record: ContentPageDto) => (
            <Space wrap>
              <EditButton hideText size="small" recordItemId={record.id} resource="content" />
              <ShowButton hideText size="small" recordItemId={record.id} resource="content" />
              <DeleteButton hideText size="small" recordItemId={record.id} resource="content" />
              {record.status === ContentStatus.DRAFT && (
                <Button size="small" onClick={() => handlePublish(record.id)}>
                  Publish
                </Button>
              )}
              {record.status === ContentStatus.PUBLISHED && (
                <Button size="small" onClick={() => handleArchive(record.id)}>
                  Archive
                </Button>
              )}
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
